import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import HeroCanvas from '../HeroCanvas/HeroCanvas';
import styles from './Banner.module.css';
import '../../styles/animations.module.css';

function Banner() {
  const navigate = useNavigate();
  const bannerRef = useRef(null);
  const eyebrowRef = useRef(null);
  const titleRef = useRef(null);
  const highlightRef = useRef(null);
  const subtitleRef = useRef(null);
  const descRef = useRef(null);
  const ctaRef = useRef(null);
  const scrollRef = useRef(null);

  // GSAP hero entrance
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const ctx = gsap.context(() => {
      if (prefersReduced) {
        gsap.set([titleRef.current, highlightRef.current, subtitleRef.current,
          descRef.current, scrollRef.current], { opacity: 1, y: 0, scale: 1, clearProps: 'all' });
        if (ctaRef.current) gsap.set(ctaRef.current.children, { opacity: 1, y: 0, clearProps: 'all' });
        return;
      }

      gsap.set(eyebrowRef.current, { opacity: 0, y: -15 });
      gsap.set(titleRef.current, { opacity: 0, y: 55 });
      gsap.set(highlightRef.current, { opacity: 0, y: 40, scale: 0.88 });
      gsap.set(subtitleRef.current, { opacity: 0, y: 28, letterSpacing: '24px' });
      gsap.set(descRef.current, { opacity: 0, y: 22 });
      if (ctaRef.current) gsap.set(ctaRef.current.children, { opacity: 0, y: 18 });
      gsap.set(scrollRef.current, { opacity: 0 });

      const tl = gsap.timeline({ delay: 0.15 });
      tl.to(eyebrowRef.current, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' })
        .to(titleRef.current, { opacity: 1, y: 0, duration: 0.85, ease: 'power3.out' }, '-=0.2')
        .to(highlightRef.current, { opacity: 1, y: 0, scale: 1, duration: 0.9, ease: 'back.out(1.5)' }, '-=0.5')
        .to(subtitleRef.current, { opacity: 1, y: 0, letterSpacing: '12px', duration: 0.7, ease: 'power2.out' }, '-=0.45')
        .to(descRef.current, { opacity: 1, y: 0, duration: 0.65, ease: 'power2.out' }, '-=0.35')
        .to(ctaRef.current?.children ?? [], { opacity: 1, y: 0, duration: 0.5, stagger: 0.13, ease: 'power2.out' }, '-=0.25')
        .to(scrollRef.current, { opacity: 1, duration: 0.5 }, '+=0.35');
    }, bannerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section id="hero" className={styles.banner} ref={bannerRef}>
      {/* Ambient glow */}
      <div className={styles.bannerGlow} />

      {/* Canvas constellation */}
      <HeroCanvas />

      {/* Content */}
      <div className={styles.bannerContentWrapper}>
        <div className={styles.bannerContent}>

          <p className={styles.bannerEyebrow} ref={eyebrowRef}>Universo Minecraft</p>

          <h1 className={styles.bannerTitle} ref={titleRef}>
            Bienvenido al
            <br />
            <span className={styles.highlight} ref={highlightRef}>Multiverso</span>
          </h1>

          <p className={styles.bannerSubtitle} translate="no" ref={subtitleRef}>
            TECNILAND
          </p>

          <p className={styles.bannerDescription} ref={descRef}>
            Un universo Minecraft inmersivo, épico y completamente personalizado
          </p>

          <div className={styles.bannerCta} ref={ctaRef}>
            <button className={styles.btnPrimary} onClick={() => navigate('/server')}>
              Muy Pronto
            </button>
            <button
              className={styles.btnSecondary}
              onClick={() => document.getElementById('intro')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Explorar ↓
            </button>
          </div>

        </div>
      </div>

      {/* Scroll indicator */}
      <div className={styles.scrollIndicator} ref={scrollRef}>
        <div className={styles.scrollArrow} />
        <span>Scroll</span>
      </div>

      {/* Bottom fade */}
      <div className={styles.bannerFade} />
    </section>
  );
}

export default Banner;
